// useAuthState Hook
// Link: /src/HelloCashflowDomain/Authentication/hooks/useAuthState.ts
// This hook provides real-time authentication state monitoring using the proven pattern

import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useState } from "react";
import FrontendLoggerFactory from 'utils/logging/FrontendLoggerFactory';

import { getFirebaseAuth } from "../services/firebase";
import type { AuthError, AuthUser } from "../types/auth.types";

const logger = FrontendLoggerFactory.getLogger().withComponent('useAuthState');

/**
 * Hook that monitors Firebase authentication state using the dependency injection pattern
 * This prevents race conditions by using the shared Firebase service
 */
export const useAuthState = () => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        try {
            const auth = getFirebaseAuth();

            // Use Firebase's onAuthStateChanged for real-time auth state monitoring
            const unsubscribe = onAuthStateChanged(auth, firebaseUser => {
                setUser(firebaseUser as AuthUser | null);
                setLoading(false);
            });

            return unsubscribe;
        } catch (error) {
            logger.error("Firebase Auth not initialized:", error as Error);
            // Avoid synchronous state update in effect
            Promise.resolve().then(() => {
                setLoading(false);
                setUser(null);
            });
            return () => {}; // Return empty cleanup function
        }
    }, []);

    return {
        user,
        loading,
        isAuthenticated: !!user,
        error: null as AuthError | null, // Error handling can be added later if needed
    };
};

export default useAuthState;
